import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Pencil, Trash2, FileText, Info, Edit3 } from 'lucide-react';
import { formatDate, generateSlug } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { MarkdownPreview } from './docs';
import { useAdminCrud } from '@/hooks/useAdminCrud';

interface DocCategory {
  id: string;
  name: string;
  slug: string;
}

interface Doc {
  id: string;
  category_id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  display_order: number | null;
  is_active: boolean | null;
  created_at: string;
  updated_at: string;
  doc_categories?: DocCategory;
}

interface DocFormData {
  title: string;
  slug: string;
  category_id: string;
  content: string;
  excerpt: string;
  display_order: number;
  is_active: boolean;
}

export function DocsManager() {
  // Keep separate state for categories (needed for dropdown)
  const [categories, setCategories] = useState<DocCategory[]>([]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('doc_categories')
        .select('id, name, slug')
        .order('display_order');
      if (data) setCategories(data);
    };
    fetchCategories();
  }, []);

  // Use shared CRUD hook for documentation
  const {
    items: docs,
    loading,
    editorOpen,
    setEditorOpen,
    editingItem: editingDoc,
    formData,
    setFormData,
    isSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete: docToDelete,
    isDeleting,
    openCreateEditor,
    openEditEditor,
    handleSave,
    confirmDelete,
    handleDelete,
  } = useAdminCrud<Doc, DocFormData>({
    table: 'documentation',
    select: `*, doc_categories (id, name, slug)`,
    orderBy: { column: 'display_order' },
    entityName: 'article',
    getInitialFormData: () => ({
      title: '',
      slug: '',
      category_id: categories[0]?.id || '',
      content: '',
      excerpt: '',
      display_order: 0,
      is_active: true,
    }),
    mapItemToForm: (doc) => ({
      title: doc.title,
      slug: doc.slug,
      category_id: doc.category_id,
      content: doc.content,
      excerpt: doc.excerpt || '',
      display_order: doc.display_order || 0,
      is_active: doc.is_active ?? true,
    }),
    buildPayload: (form) => ({
      title: form.title,
      slug: form.slug || generateSlug(form.title),
      category_id: form.category_id,
      content: form.content,
      excerpt: form.excerpt || null,
      display_order: form.display_order,
      is_active: form.is_active,
    }),
    validatePayload: (form) => {
      if (!form.title) return 'Title is required';
      if (!form.category_id) return 'Category is required';
      return null;
    },
  });

  // Group docs by category
  const docsByCategory = docs.reduce((acc, doc) => {
    const categoryName = doc.doc_categories?.name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(doc);
    return acc;
  }, {} as Record<string, Doc[]>);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Documentation"
        count={docs.length}
        onAdd={openCreateEditor}
        addLabel="Add Article"
      />

      {loading ? (
        <AdminTableSkeleton columns={5} rows={4} />
      ) : docs.length === 0 ? (
        <AdminEmptyState
          icon={FileText}
          title="No documentation yet"
          description="Create documentation articles to help users understand your platform."
          actionLabel="Add Article"
          onAction={openCreateEditor}
        />
      ) : (
        <div className="space-y-6">
          {Object.entries(docsByCategory).map(([categoryName, categoryDocs]) => (
            <div key={categoryName} className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground px-1">{categoryName}</h3>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-20">Order</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead className="w-32">Updated</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryDocs.map((doc) => (
                      <TableRow 
                        key={doc.id}
                        className="cursor-pointer"
                        onClick={() => openEditEditor(doc)}
                      >
                        <TableCell>
                          <div className="font-medium">{doc.title}</div>
                          {doc.excerpt && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {doc.excerpt}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground">
                            {doc.display_order ?? 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={doc.is_active ? 'default' : 'secondary'}>
                            {doc.is_active ? 'Published' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(doc.updated_at)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div 
                            className="flex items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditEditor(doc)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => confirmDelete(doc)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingDoc ? 'Edit Article' : 'Add Article'}
        description={editingDoc ? 'Update the article content.' : 'Create a new documentation article.'}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={editingDoc ? 'Update' : 'Create'}
        size="xl"
      >
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details" className="gap-2">
              <Info className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <Edit3 className="h-4 w-4" />
              Content
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Getting Started Overview"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                placeholder="auto-generated if empty"
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief description of this article..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_active">Published</Label>
                <p className="text-xs text-muted-foreground">Show this article on the public site</p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-4">
            <MarkdownPreview
              value={formData.content}
              onChange={(content) => setFormData({ ...formData, content })}
              placeholder="Write your documentation content using Markdown..."
              minHeight="500px"
              storageBucket="docs"
              storageFolder="images"
            />
          </TabsContent>
        </Tabs>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={docToDelete?.title}
        itemType="article"
        isDeleting={isDeleting}
      />
    </div>
  );
}
